// TimelineNoteCreate.tsx - Standalone note creation for UnifiedTimeline
// This version doesn't depend on ListContext from react-admin

import {
    CreateBase,
    Form,
    useGetIdentity,
    useNotify,
    useRecordContext,
    useRefresh,
    useUpdate,
    type Identifier,
} from "ra-core";
import { useFormContext } from "react-hook-form";
import { SaveButton } from "@/components/admin/form";
import { cn } from "@/lib/utils";

import { NoteInputs } from "../notes/NoteInputs";

const getCurrentDate = () => {
    const now = new Date();
    return now.toISOString();
};

export const TimelineNoteCreate = ({
    showStatus,
    className,
}: {
    showStatus?: boolean;
    className?: string;
}) => {
    const record = useRecordContext();
    const { identity } = useGetIdentity();
    const refresh = useRefresh();

    if (!record || !identity) return null;

    return (
        <CreateBase resource="contactNotes" redirect={false}>
            <Form>
                <div className={cn("space-y-3", className)}>
                    <NoteInputs showStatus={showStatus} />
                    <TimelineNoteCreateButton
                        contactId={record.id as Identifier}
                        onSuccess={refresh}
                    />
                </div>
            </Form>
        </CreateBase>
    );
};

const TimelineNoteCreateButton = ({
    contactId,
    onSuccess,
}: {
    contactId: Identifier;
    onSuccess: () => void;
}) => {
    const [update] = useUpdate();
    const notify = useNotify();
    const { identity } = useGetIdentity();
    const { reset } = useFormContext();

    if (!identity) return null;

    const resetValues = {
        date: getCurrentDate(),
        text: null,
        attachments: null,
        status: "warm",
    };

    const handleSuccess = (data: any) => {
        reset(resetValues, { keepValues: false });
        onSuccess();
        // Update the contact's last_seen timestamp
        update("contacts", {
            id: contactId,
            data: { last_seen: new Date().toISOString(), status: data.status },
        });
        notify("Note added");
    };

    return (
        <div className="flex justify-end">
            <SaveButton
                type="button"
                label="Add note"
                transform={(data) => ({
                    ...data,
                    contact_id: contactId,
                    sales_id: identity.id,
                    date: data.date || getCurrentDate(),
                })}
                mutationOptions={{
                    onSuccess: handleSuccess,
                }}
            >
                Add note
            </SaveButton>
        </div>
    );
};

export default TimelineNoteCreate;
